import {
    useNavigate
} from "react-router-dom";

import './fuq-list.scss';

import IconVisibility from '../../../../../components/icons/icon-visibility/icon-visibility';
import IconEdit from '../../../../../components/icons/icon-edit/icon-edit';


import Icon from "../../../../../components/icons/icon/icon";
import { ButtonIcon } from "../../../../../components/inputs/button-icon/button-icon";
import GridLineLayout from "../../../../../components/layouts/grid-line-layout/grid-line-layout";
import GridLine, { BackgroundColor } from "../../../../../components/layouts/grid-line-layout/grid-line/grid-line";
import GridLineItem from "../../../../../components/layouts/grid-line-layout/grid-line/grid-line-item/grid-line-item";
import GridLineIndicators from "../../../../../components/layouts/grid-line-layout/grid-line/grid-line-indicators/grid-line-indicators";
import GridLineButtons from "../../../../../components/layouts/grid-line-layout/grid-line/grid-line-buttons/grid-line-buttons";
import { FOLLOW_UP_QUESTION_TYPE, FOLLOW_UP_QUESTION_TYPE_MAP } from "../../../constants";
import FollowUpQuestionStore from "../../../stores/follow-up-question-store";
import FontBody1 from "../../../../../components/fonts/font-body1/font-body1";
import { FONT_COLOR, FONT_VARIANT } from "../../../../../components/fonts/font-props";
import QuestionTypeViewer from "../../follow-up-question/question-types/question-type-viewer";
import { Tooltip, TOOLTIP_VARIANTS } from "../../../../../components/tooltip/tooltip";
import FontBody2 from "../../../../../components/fonts/font-body2/font-body2";
import IconDelete from "../../../../../components/icons/icon-delete/icon-delete";
import { useState } from "react";
import { ConfirmModal } from "../../../../../components/modal/components/confirm-modal/confirm-modal";

import { List } from 'react-movable';
import IconSort from "../../../../../components/icons/icon-sort/icon-sort";
import IconTranslationIndicator from "../../../../../components/icons/icon-translation-indicator/icon-translation-indicator";
import useTranslation from "../../../../../translation/hooks/translation-hook";

interface IFUQListProps {
    list?: FollowUpQuestionStore[];
    onQuestionDelete: (question: FollowUpQuestionStore) => void;
}

const FUQList = ({ list, onQuestionDelete }: IFUQListProps) => { 
    const navigate = useNavigate();
    const { hasMissingTranslations } = useTranslation();

    const questionsList = list ?? [];
    const listLen = questionsList.length.toString();
    const [questionToDelete, setQuestionToDelete] = useState(undefined as FollowUpQuestionStore | undefined);

    const onQuestionClicked = (question: FollowUpQuestionStore) => navigate({
        pathname: question.id.toString()
    });

    const getVisibilityIconTooltipVariant = (question: FollowUpQuestionStore) => {
        return question.isEnabled ? TOOLTIP_VARIANTS.SUCCESS : TOOLTIP_VARIANTS.INFO;
    };

    const getVisibilityIconTooltipContent = (question: FollowUpQuestionStore) => {
        return question.isEnabled
            ? <FontBody2 color={FONT_COLOR.NEUTRAL_WHITE}>This question will be <br /> presented to customers</FontBody2>
            : <FontBody2 color={FONT_COLOR.NEUTRAL_WHITE}>This question will NOT be <br /> presented to customers</FontBody2>;
    };

    const onSortEnd = (oldIndex: number, newIndex: number) => {
        /** if there was a try to reorder, but cancelled */
        if (oldIndex === newIndex) {
            return;
        }

        questionsList?.forEach((question, index) => {
            var down = oldIndex > newIndex;
            var changedIndex = down ? (index + 1) : (index - 1);
            var range = down ? (index <= oldIndex && index >= newIndex) : (index >= oldIndex && index <= newIndex);
            let newWeight = index;

            if (range) {
                newWeight = (index === oldIndex) ? newIndex : changedIndex;
            }
            question.setWeight(newWeight);         
        });
    };

    return (
        <>
            <List
                values={questionsList}
                onChange={({ oldIndex, newIndex }) => onSortEnd(oldIndex, newIndex)}
                renderList={({ children, props }) => 
                    <div className="return-reason-fuqs">
                        <GridLineLayout
                            title={'Follow-up questions (' + listLen + ')'}
                            subtitle="Add questions that customers will have to answer after the customer will choose this reason"                            
                        >
                            <div className={`grid-line-sorted-list`} {...props}>
                                { children }
                            </div>
                        </GridLineLayout>
                    </div>}
                renderItem={({ value, props }) => 
                    <div className={`grid-line-holder`} {...props}>
                        <GridLine
                            onClick={() => onQuestionClicked(value)}
                            backgroudColor={BackgroundColor.LIGHT} key={value.id}
                            settings={{ columns: [{ width: 30, type: 'px'}, { width: 2.5 }, { width: 2 }, { width: 90, type: 'px' }, { width: 80, type: 'px' }] }}
                            isExpandable={true}
                            expandableContent={<QuestionTypeViewer question={value} disabled />}
                            columns={[

                                <GridLineItem>
                                    <div data-movable-handle="true" className="item-move">
                                        <Icon Component={IconSort} />
                                    </div>
                                </GridLineItem>,

                                <GridLineItem>
                                    <FontBody1 color={FONT_COLOR.PRIMARY2} variant={FONT_VARIANT.BOLD}>{value.name}</FontBody1>
                                </GridLineItem>,

                                <GridLineItem>
                                    <div className="fuq-type">
                                        <Icon Component={FOLLOW_UP_QUESTION_TYPE_MAP[value.type as FOLLOW_UP_QUESTION_TYPE].icon} />
                                        <FontBody1>{FOLLOW_UP_QUESTION_TYPE_MAP[value.type as FOLLOW_UP_QUESTION_TYPE].label}</FontBody1>
                                    </div>
                                </GridLineItem>,

                                <GridLineIndicators>
                                    {hasMissingTranslations(undefined, value.getTranslationKeys()) 
                                        && <Tooltip 
                                                content={
                                                    <FontBody2 color={FONT_COLOR.NEUTRAL_WHITE}>
                                                        There are untranslated items. Please edit <br /> 
                                                        the follow-up question for more information.
                                                    </FontBody2>
                                                }
                                                variant={TOOLTIP_VARIANTS.ERROR}
                                            >
                                                <Icon Component={IconTranslationIndicator} />
                                            </Tooltip>
                                    }
                                    <Tooltip
                                        variant={getVisibilityIconTooltipVariant(value)}
                                        content={getVisibilityIconTooltipContent(value)}
                                    >
                                        <Icon Component={IconVisibility} disabled={!value.isEnabled} />
                                    </Tooltip>
                                </GridLineIndicators>,

                                <GridLineButtons>
                                    <ButtonIcon Component={IconDelete} onClick={() => setQuestionToDelete(value)} />
                                    <ButtonIcon Component={IconEdit} onClick={() => onQuestionClicked(value)} />
                                </GridLineButtons>,

                            ]}
                        />
                    </div>
                }
            />

            {questionToDelete &&
                <ConfirmModal
                    title="Delete follow-up question"
                    text={<>You asked to <strong>delete</strong> the follow-up question <strong>{questionToDelete.name}</strong></>}
                    implicationText="Once deleted the question and all its translations can’t be restored."
                    questionText="Are you sure you want to delete this follow-up question?"
                    cancelButton={{
                        title: 'Cancel',
                        onClick: () => {
                            setQuestionToDelete(undefined);
                        }
                    }}
                    okButton={{
                        title: 'Delete',
                        onClick: () => {
                            onQuestionDelete(questionToDelete);
                            setQuestionToDelete(undefined);
                        }
                    }}
                />
            }
        </>
    )
};

export default FUQList;
